import React, { useLayoutEffect, useState } from 'react';

import type { PageDimensions } from '@aline/common';
import {
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormLabel,
  InputLabel,
  RadioGroup,
} from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import {
  AlineButton,
  AlineDialog,
  AlineRadio,
  CheckMarkMenuItem,
  ToolbarInput,
  ToolbarSelect,
} from 'src/components';
import type {
  Orientation,
  PageSize,
  PageSizeName,
} from 'src/editor/toolbars/tabs/layout-tab/utils';
import {
  getPageSizeByName,
  getPageSizeByPageDimensions,
  pageSizes,
  parseValue,
} from 'src/editor/toolbars/tabs/layout-tab/utils';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  pageSetupInput: {
    height: 32,
    marginTop: theme.spacing(2),
    width: 72,
  },
  pageSetupLabel: {
    ...theme.appTypography.fieldLabel,
  },
  pageSetupLabelUnit: {
    color: theme.appColors.secondaryType,
  },
  pageSetupMarginFields: {
    display: 'flex',
  },
  pageSetupMarginLabel: {
    lineHeight: `calc(${theme.spacing(4)} + 2px)`,
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  pageSetupMargins: {
    marginLeft: theme.spacing(6),
  },
  pageSetupOrientation: {
    flexDirection: 'row',
  },
  pageSetupOrientationButton: {
    '&:first-child': {
      marginLeft: 0,
    },
    marginLeft: theme.spacing(3),
    marginRight: 0,
  },
  pageSetupPaperSize: {
    marginTop: theme.spacing(3),
  },
}));

export function PageSetupDialog(props: {
  onChange: (pageDimensions: PageDimensions) => void;
  onClose: () => void;
  open: boolean;
  pageDimensions: PageDimensions;
}): JSX.Element {
  const { pageDimensions } = props;
  const { classes } = useStyles();
  const cannotEdit = useEditingDisabled();

  const [pageSizeSelectOpen, setPageSizeSelectOpen] = useState<boolean>(false);
  const [marginBottom, setMarginBottom] = useState<string>(pageDimensions.marginBottom.toString());
  const [marginLeft, setMarginLeft] = useState<string>(pageDimensions.marginLeft.toString());
  const [marginRight, setMarginRight] = useState<string>(pageDimensions.marginRight.toString());
  const [marginTop, setMarginTop] = useState<string>(pageDimensions.marginTop.toString());
  const [orientation, setOrientation] = useState<Orientation>(
    pageDimensions.width < pageDimensions.height ? 'portrait' : 'landscape'
  );

  const [pageSize, setPageSize] = useState<PageSizeName>(
    getPageSizeByPageDimensions(pageDimensions).name
  );

  function resetState(): void {
    setMarginBottom(pageDimensions.marginBottom.toString());
    setMarginLeft(pageDimensions.marginLeft.toString());
    setMarginRight(pageDimensions.marginRight.toString());
    setMarginTop(pageDimensions.marginTop.toString());
    setOrientation(pageDimensions.width < pageDimensions.height ? 'portrait' : 'landscape');
    setPageSize(getPageSizeByPageDimensions(pageDimensions).name);
  }

  useLayoutEffect(() => {
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDimensions]);

  return (
    <AlineDialog open={props.open} title="Page setup" onClose={props.onClose}>
      <DialogContent>
        <div>
          <div>
            <FormLabel className={classes.pageSetupLabel}>Orientation</FormLabel>
            <RadioGroup
              classes={{ root: classes.pageSetupOrientation }}
              value={orientation}
              onChange={(event): void => setOrientation(event.target.value as Orientation)}
            >
              <FormControlLabel
                classes={{ root: classes.pageSetupOrientationButton }}
                control={<AlineRadio disabled={cannotEdit} size="small" />}
                label="Portrait"
                value="portrait"
              />
              <FormControlLabel
                classes={{ root: classes.pageSetupOrientationButton }}
                control={<AlineRadio disabled={cannotEdit} size="small" />}
                label="Landscape"
                value="landscape"
              />
            </RadioGroup>
          </div>
          <div className={classes.pageSetupPaperSize}>
            <FormLabel className={classes.pageSetupLabel}>Paper size</FormLabel>
            <div>
              <ToolbarSelect
                input={
                  <ToolbarInput
                    disabled={cannotEdit}
                    inputStyles={{ height: 32, margin: 0, width: '100%' }}
                    open={pageSizeSelectOpen}
                  />
                }
                menuAttribute="page-setup-dialog-page-size-select-menu"
                open={pageSizeSelectOpen}
                selectAttribute="page-setup-dialog-page-size-select-select"
                spanAttribute="page-setup-dialog-page-size-select-span"
                value={pageSize}
                onClose={(): void => setPageSizeSelectOpen(false)}
                onOpen={(): void => setPageSizeSelectOpen(true)}
                onSelectChange={(event): void => setPageSize(event.target.value as PageSizeName)}
              >
                {pageSizes.map((ps: PageSize) => {
                  return (
                    <CheckMarkMenuItem
                      key={ps.name}
                      disabled={cannotEdit}
                      selected={ps.name === pageSize}
                      value={ps.name}
                    >
                      {ps.name} ({ps.width}” x {ps.height}”)
                    </CheckMarkMenuItem>
                  );
                })}
              </ToolbarSelect>
            </div>
          </div>
        </div>
        <div className={classes.pageSetupMargins}>
          <FormLabel className={classes.pageSetupLabel}>
            Margins <span className={classes.pageSetupLabelUnit}>(inches)</span>
          </FormLabel>
          <div className={classes.pageSetupMarginFields}>
            <div>
              <InputLabel className={classes.pageSetupMarginLabel} htmlFor="page-setup-margin-top">
                Top
              </InputLabel>
              <InputLabel
                className={classes.pageSetupMarginLabel}
                htmlFor="page-setup-margin-bottom"
              >
                Bottom
              </InputLabel>
              <InputLabel
                className={classes.pageSetupMarginLabel}
                htmlFor="page-setup-margin-right"
              >
                Right
              </InputLabel>
              <InputLabel className={classes.pageSetupMarginLabel} htmlFor="page-setup-margin-left">
                Left
              </InputLabel>
            </div>
            <div>
              <AlineInput
                disabled={cannotEdit}
                id="page-setup-margin-top"
                inputClassName={classes.pageSetupInput}
                value={marginTop}
                onChange={(event): void => setMarginTop(event.target.value)}
              />
              <AlineInput
                disabled={cannotEdit}
                id="page-setup-margin-bottom"
                inputClassName={classes.pageSetupInput}
                value={marginBottom}
                onChange={(event): void => setMarginBottom(event.target.value)}
              />
              <AlineInput
                disabled={cannotEdit}
                id="page-setup-margin-right"
                inputClassName={classes.pageSetupInput}
                value={marginRight}
                onChange={(event): void => setMarginRight(event.target.value)}
              />
              <AlineInput
                disabled={cannotEdit}
                id="page-setup-margin-left"
                inputClassName={classes.pageSetupInput}
                value={marginLeft}
                onChange={(event): void => setMarginLeft(event.target.value)}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={props.onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={cannotEdit}
          variant="contained"
          onClick={(): void => {
            resetState();

            const newPageSize = getPageSizeByName(pageSize, orientation === 'landscape');
            const newPageDimensions = {
              ...pageDimensions,
              height: newPageSize.height,
              marginBottom: parseValue(marginBottom) ?? pageDimensions.marginBottom,
              marginLeft: parseValue(marginLeft) ?? pageDimensions.marginLeft,
              marginRight: parseValue(marginRight) ?? pageDimensions.marginRight,
              marginTop: parseValue(marginTop) ?? pageDimensions.marginTop,
              width: newPageSize.width,
            };
            props.onChange(newPageDimensions);
          }}
        >
          Ok
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
